<template>
  <div class="l-main">
    <div class="l-main__header">
      <ul class="c-breadcrumbs">
        <li class="c-breadcrumbs__item">
          <a
            class="c-breadcrumbs__link c-breadcrumbs__home c-breadcrumbs__home--disabled"
            @click="goToHome"
          > 
            <md-icon>home</md-icon>
            <span>Home</span>
          </a>
        </li>
        <li class="c-breadcrumbs__item--separator">
          /
        </li>

        <li class="c-breadcrumbs__item is-current">
          <a class="c-breadcrumbs__link">Users</a>
        </li>
      </ul>
    </div>
    <div class="l-main__ctn u-box">
      <div class="c-page-header">
        <div>
          <h2 class="c-page-header__title">
            Users
          </h2>
          <p class="c-page-header__subtitle">
            Manage who can access the backoffice and what they can do.
          </p>
        </div>

        <div class="c-page-header__actions">
          <button
            type="button"
            class="ui-btn ui-btn--primary"
            @click="createUser()"
          >
            <app-icon name="person_add" />
            Add user
          </button>
        </div>
      </div>

      <div class="c-page-toolbar">
        <div class="ui-field c-page-toolbar__search">
          <div class="ui-field__control ui-field__control--with-action">
            <app-icon
              class="ui-field__icon"
              name="search"
            />
            <input
              v-model="searchQuery"
              class="ui-field__input"
              type="text"
              placeholder="Search by email…"
              aria-label="Search users"
              @keydown.enter="getUsers(1)"
            >
            <button
              v-if="hasPendingSearch"
              type="button"
              class="ui-field__action"
              title="Search"
              aria-label="Search"
              @click="getUsers(1)"
            >
              <app-icon name="keyboard_return" />
            </button>
            <button
              v-else-if="searchQuery"
              type="button"
              class="ui-field__action"
              title="Clear search"
              aria-label="Clear search"
              @click="resetSearch()"
            >
              <app-icon name="close" />
            </button>
          </div>
        </div>

        <span
          v-if="fetchedResults"
          class="c-page-toolbar__meta"
        >
          {{ totalLabel }}
        </span>
      </div>

      <div class="c-table-wrapper">
        <base-table-component
          :prop-data="users"
          sortable
          :empty-title="previousSearchQuery ? 'No users found' : 'No users yet'"
          :empty-description="previousSearchQuery ? `Nothing matches “${previousSearchQuery}”. Try a different email.` : 'Add a user to give them access to the backoffice.'"
          @sort="handleSort"
        >
          <template
            v-slot:default="slotProps"
          >
            <span class="c-table__btn-wrap">
              <router-link
                v-if="canEditUser(slotProps.selected.rowData)"
                :to="{ name: 'users-edit', params: { id: slotProps.selected.rowData.id } }"
              >
                <generic-button
                  icon
                  transparent
                  text-inherit
                >
                  <md-tooltip md-direction="top">
                    Edit
                  </md-tooltip>
                  <img src="../../assets/edit-button.svg" alt="">
                </generic-button>
              </router-link>
              <!-- keeps the delete buttons aligned on rows that can't be edited -->
              <span
                v-else
                class="c-users__btn-spacer"
              />
            </span>
            <span class="c-table__btn-wrap">
              <generic-button
                v-if="user.id !== slotProps.selected.rowData.id && user.role === 1"
                icon
                transparent
                text-inherit
                @click="openDeleteModal(slotProps.selected.rowData)"
              >
                <md-tooltip md-direction="top">
                  Delete
                </md-tooltip>
                <img src="../../assets/delete-button.svg" alt="">
              </generic-button>
            </span>
          </template>
        </base-table-component>
      </div>
      <paginate
        v-if="shouldShowPagination"
        :page-count="paginationData.pageCount"
        :force-page="currentPage"
        :page-range="limitPerPage"
        :click-handler="getNextPage"
        :prev-text="'‹'"
        :next-text="'›'"
        :container-class="'c-pagination'"
        :prev-link-class="'md-button prev md-elevation-1'"
        :next-link-class="'md-button next md-elevation-1'"
        :page-link-class="'md-button md-elevation-1'"
        :active-class="'active'"
        :disabled-class="'disabled'"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations  } from "vuex";
import CreateUserModal from "./create-update";
import { userService } from '../../services/user';
import { paginationService } from '../../services/pagination/index';
import BaseTableComponent from '../../shared/table/BaseTableComponent';
import DeleteModalComponent from '../../shared/delete-modal/DeleteModalComponent';
import GenericButton from '../../shared/buttons/GenericButton';
import AppIcon from '../../shared/app-icon/AppIcon';
import { get } from 'lodash';
import { userRoles } from '../../const/roles';

export default {
  components: {
    BaseTableComponent,
    GenericButton,
    AppIcon,
  },
  data() {
    return {
      users: [],
      paginationData: {},
      limitPerPage: 10,
      currentPage: 1,
      userRoles,
      searchQuery: '',
      previousSearchQuery: null,
      sortColumnActive: '',
      sortColumnDirection: '',
      fetchedResults: false,
    }
  },

  computed: {
    ...mapGetters("account", ["user"]),

    hasPendingSearch() {
      return this.searchQuery !== (this.previousSearchQuery || '');
    },

    totalLabel() {
      const total = this.paginationData.totalItems || 0;

      return `${total} ${total === 1 ? 'user' : 'users'}`;
    },

    shouldShowPagination() {
      return this.paginationData.totalItems > this.limitPerPage
    },
  },

  async created() {
    this.getUsers();
  },

  methods: {
    ...mapMutations('user', ['FETCHED_USERS']),
    createUser() {
      this.$modal.show(
        CreateUserModal,
        {
          userData: userService.formatUserDataData(),
          pageCount: this.paginationData.pageCount,
          currentPage: this.currentPage,
          getNextPage: this.getNextPage
        },
        { height: "auto", scrollable: true }
      );
    },

    goToHome() {
      this.$router.push({ name: 'home' });
    },

    async getUsers(currentPage = 1, limit = 10, force = false) {
      if (
        !force &&
        (this.searchQuery === this.previousSearchQuery && 
        this.limitPerPage === limit && 
        this.currentPage === currentPage)
      ) {
        return;
      }

      this.currentPage = currentPage;
      const searchConstraints = {
        ...(this.searchQuery ? { term: this.searchQuery} : {}),
        ...(this.currentPage ? { page: this.currentPage} : {}),
        ...(this.sortColumnActive ? { sortColumn: this.sortColumnActive } : {}),
        ...(this.sortColumnDirection ? { sortDirection: this.sortColumnDirection } : {}),
        limit
      }
      const response = await userService.getUsers(searchConstraints);

      if (this.shouldGoBackOnePage(response)) {
        this.getNextPage(this.currentPage - 1);

        return;
      }

      this.FETCHED_USERS(get(response, 'data.items', []));
      this.paginationData = paginationService.getPaginationData(response.data);
      this.users = userService.prepareUsersDataFromResponse(response.data.items);
      this.previousSearchQuery = this.searchQuery;
      this.fetchedResults = true;
    },

    canEditUser(row) {
      return this.user.id !== row.id && this.user.role === 1 && row.role === userRoles.manager;
    },

    getNextPage(page) {
      this.currentPage = page
      this.getUsers(page, this.limitPerPage, true);
    },

    async deleteUser(id) {
      return await userService.deleteUser(id);
    },

    openDeleteModal(selected) {
      this.$modal.show(
        DeleteModalComponent,
        {
          deleteMsg: `user with email "${selected.email}`,
          propData: selected.id,
          deleteFunction: this.deleteUser,
          getDataFunction: this.getNextPage,
          pageData: {currentPage: this.currentPage, limit: this.limitPerPage }
        },
        { height: "auto", scrollable: false, width: "440px" }
      )
    },

    isOnLine() {
      return window.location.pathname.includes('on-line');
    },

    handleSort(column, direction) {
      this.sortColumnActive = column;
      this.sortColumnDirection = direction.toUpperCase();
      this.getUsers(this.currentPage, this.limitPerPage, true);
    },

    resetSearch() {
      this.searchQuery = '';
      this.getUsers(1, this.limitPerPage, true);
    },


    shouldGoBackOnePage(response) {
      return response.data.items.length === 0 && response.data.pageCount < this.currentPage && response.data.pageCount !== 0;
    },
  }
};
</script>

<style lang="scss" scoped>
  .c-users__btn-spacer {
    display: inline-block;
    width: 32px;
  }
</style>